import { Badge, Box, IconButton, Snackbar, Stack, Tooltip } from '@mui/material';
import { useAtom } from 'jotai';
import * as React from 'react';
import { baseAtom, getValidFindLinks, selectCanvasLink } from '../../stores/jotai/base';
import { canvasAtom, setSelectedHypothesisIDToFilter } from '../../stores/jotai/canvas/canvas';
import { canvasDataAtom } from '../../stores/jotai/canvas/canvasData';
import { hasPermissionCanvasWrite } from '../../stores/jotai/permissions';
import { colors } from './../../../lib/teacup/lib/colors';
import { CUSTOMER_SEGMENTS_CATEGORY_NAME } from './constants';
import CommentIcon from "../../../icons/Comment"
import ThumbsUpIcon from "../../../icons/ThumbsUpIcon"
import ThumbsDownIcon from "../../../icons/ThumbsDownIcon"
import LinkIcon from "../../../icons/LinkIcon"
import LinikedShowHypothesesIcon from "../../../icons/ShowLinkedHypothesesIcon"
import LinikedHideHypothesesIcon from "../../../icons/HideLinkedHypothesesIcon" 
import ValidateIcon from "../../../icons/ValidateIcon"
import InvalidateIcon from "../../../icons/InvalidateIcon"
import DeleteOutlinedIcon from "../../../icons/DeleteOutlinedIcon"

export default function MaterialEntryToolbar(props) {
    const { comments, openDeleteEntryModal,findingLinksData } = props;
    const [base, setBase] = useAtom(baseAtom);
    const [canvasData,setCanvasData]=useAtom(canvasDataAtom);
    const [canvasState, setCanvasState] = useAtom(canvasAtom);
    const [localError, setLocalError] = React.useState(null);
    const canEditCanvas = hasPermissionCanvasWrite(base);
    const hasAnyFindings = (props?.attachedFindings?.length || 0) > 0;
    const [thumbsUpHoverd,setThumbsUpHoverd]=React.useState(false);
    const [thumbsDownHoverd,setThumbsDownHoverd]=React.useState(false);
    const [validateHoverd,setValidateHoverd]=React.useState(false);
    const [invalidateHoverd,setInvalidateHoverd]=React.useState(false);
    const [linkHoverd,setLinkHoverd]=React.useState(false);
    const [commentHoverd,setCommentHoverd]=React.useState(false);

    const handleEntryStateChange = (state) => {
        if (canEdit()) {
            if (props?.entry?.state?.name === state) {
                props.onChangeState("Hypothesis");
            } else {
                props.onChangeState(state);
            }
        } else {
            setLocalError("Only team members can validate & invalidate hypothesis");
        }
    };

    const determineColorForIcon = (icon) => {
        if (icon === "close") return "#f59896 !important";

        // Otherwise fall back to green
        return "rgb(165, 214, 167) !important";
    };

    const stateIcon = (b: any, name: string, icon: string, tooltip: string) => {
        const iconWidget = (
          <IconButton
            sx={{
              ...b,
              width:"30px",
              margin: "0 0 0 3px",
              borderRadius: 0,
              // backgroundColor: determineColorForIcon(icon),
            }}
            disableRipple
            onClick={() => handleEntryStateChange(name)}
          >
            {icon === "close" ? (
              <InvalidateIcon
                hovered={invalidateHoverd}
                onMouseEnter={() => setInvalidateHoverd(true)}
                onMouseLeave={() => setInvalidateHoverd(false)}
                sx={{ color: "black",}}
              />
            ) : (
              <ValidateIcon
                hovered={validateHoverd}
                onMouseEnter={() => setValidateHoverd(true)}
                onMouseLeave={() => setValidateHoverd(false)}
                sx={{ color: "black" }}
              />
            )}
          </IconButton>
        );

        return (
            <Tooltip
                placement="top"
                title={tooltip}
                PopperProps={{
                    sx: {
                        ...tooltipCommonAttributes,
                        margin: "12px 0px !important",
                        padding: "0px !important",
                        "& .MuiTooltip-tooltip": {
                            margin: "0px !important",
                        },
                    },
                }}
            >
                {iconWidget}
            </Tooltip>
        );
    };

    const getEntry = () => {
        return props?.entry;
    };

    const getCurrentBadFindingLinkCount = () => {
        const filteredFindings=getValidFindLinks(findingLinksData,props?.entry?.findingLinks);
        return filteredFindings?.filter((finding) => !finding.supports)?.length || 0;
    };

    const getCurrentGoodFindingLinkCount = () => {
        const filteredFindings=getValidFindLinks(findingLinksData,props?.entry?.findingLinks);
        return filteredFindings?.filter((finding) => !!finding.supports)?.length || 0;
    };

    const preProcessToolbarClick = () => {
        try {
            props.barRef.current.scrollIntoView({ block: "center" });
        } catch (e) {}
    };

    const handleSetLinkHover = (target) => {
        if (target) {
            const entry = getEntry();

            //@ts-ignore
            selectCanvasLink(setBase, entry?.entryID);
        }
    };

    const HEIGHT = "30px";

    let actionToolbarStyle: any = {
        display: "block",
        backgroundColor: "white",
        border: "1px solid rgb(130,130,130)",
        borderRadius: "2px",
        height: HEIGHT,
    };

    const badgeStyle = {
        backgroundColor: "#bdbdbd !important",
        color: "rgba(0, 0, 0, 0.87) !important",
        transform: "scale(1) translate(50%, -50%)",
        fontSize: "10px",
        lineHeight: "14px",
        borderRadius: "100px",
        padding: "2px",
        top: "-3px",
        right: "2px",
    };

    let boxStyle = {
        color: "black",
        height: "100%",
        width: "28px",
        margin: "0 2px",
        padding: "0",
        cursor: "pointer",
        "& :hover": {
            color: "inherit",
        },
        "& .MuiBadge-badge:hover": badgeStyle,
    };

    const tooltipCommonAttributes = {
        zIndex: "1700 !important",
    };

    const canEdit = () => {
        if (props.isDisabled === true) {
            return false;
        }

        return true;
    };

    const openCommentPopover = () => {
        preProcessToolbarClick();
        props.openCommentPopover();
    };

    const openInsightPopover = () => {
        if (hasAnyFindings) {
            preProcessToolbarClick();
            props.openInsightPopover();
        }
    };

    const openLinkPopover = () => {
        const { entry } = props;
        const { name: categoryName } = entry?.category;

        if (categoryName === CUSTOMER_SEGMENTS_CATEGORY_NAME) {
            handleLinkedEntryDisplayFeature();
        } else {
            if (canEditCanvas || (!canEditCanvas && countOfLinkedEntries > 0)) {
                preProcessToolbarClick();
                handleSetLinkHover(true);
                props.openLinkPopover();
            }
        }
    };

    const VALIDATE_ACTION_TOOLBAR_ITEM = stateIcon(
        boxStyle,
        "Validated",
        "check",
        "Validate Hypothesis (click again to undo)"
    );

    const INVALIDATE_ACTION_TOOLBAR_ITEM = stateIcon(
        boxStyle,
        "Invalidated",
        "close",
        "Invalidate Hypothesis (click again to undo)"
    );

    const commentIcon = <CommentIcon
        hovered={commentHoverd}
        onMouseEnter={() => setCommentHoverd(true)}
        onMouseLeave={() => setCommentHoverd(false)}
        sx={{
            color: "black",
            "&:hover": {
                color: "#1976D2",
            },
            fill: "none",  
        }}
    />;

    const COMMENT_ACTION_TOOLBAR_ITEM = (
        <Tooltip
            placement="top"
            title="Add or View Feedback"
            PopperProps={{
                sx: {
                    ...tooltipCommonAttributes,
                    margin: "12px 0px !important",
                    padding: "0px !important",
                    "& .MuiTooltip-tooltip": {
                        margin: "0px !important",
                    },
                },
            }}
        >
            <IconButton
                sx={{...boxStyle}}
                disableRipple
                onClick={openCommentPopover}
            >
                {comments?.length > 0 ? (
                    <Badge
                        badgeContent={comments?.length}
                        sx={{
                            ".MuiBadge-badge": badgeStyle,
                        }}
                    >
                        {commentIcon}
                    </Badge>
                ) : (
                    commentIcon
                )}
            </IconButton>
        </Tooltip>
    );

    const insightThumbsUpIcon = (
        <ThumbsUpIcon
            hovered={thumbsUpHoverd}
            onMouseEnter={() => setThumbsUpHoverd(true)}
            onMouseLeave={() => setThumbsUpHoverd(false)}
            sx={{
                width: "36px",
                color: colors.CHARTS_GREEN,
                ":hover": {
                    color: `#1976D2 !important`,
                    borderColor:""
                },
            }}
        />
    );

    const insightThumbsDownIcon = (
        <ThumbsDownIcon
            hovered={thumbsDownHoverd}
            onMouseEnter={() => setThumbsDownHoverd(true)}
            onMouseLeave={() => setThumbsDownHoverd(false)}

            sx={{
                // margin:'5% 0 0 6%',
                width: "28px",
                color: colors.CHARTS_RED,
                ":hover": {
                    color: `${colors.CHARTS_RED} !important`,
                },
            }}
        />
    );

    const INSIGHT_ACTION_TOOLBAR_ITEM = (
        <Tooltip
            placement="top"
            title={hasAnyFindings ? "View Linked Insights" : "No linked Insights"}
            PopperProps={{
                sx: {
                    ...tooltipCommonAttributes,
                    margin: "12px 0px !important",
                    padding: "0px !important",
                    "& .MuiTooltip-tooltip": {
                        margin: "0px !important",
                    },
                },
            }}
        >
            <IconButton
                sx={{
                    ...boxStyle,
                    width: "auto",
                    cursor: hasAnyFindings ? "pointer" : "auto",
                }}
                disableRipple
                onClick={openInsightPopover}
            >
                {getCurrentGoodFindingLinkCount() > 0 ? (
                    <Badge
                        badgeContent={getCurrentGoodFindingLinkCount()}
                        sx={{
                            "& .MuiBadge-badge": {
                                ...badgeStyle,
                                top: "-3.5px", 
                                right:'9px'
                              },
                        }}
                    >
                        {insightThumbsUpIcon}
                    </Badge>
                ) : (
                  insightThumbsUpIcon
                )}
                {getCurrentBadFindingLinkCount() > 0 ? (
                    <Badge
                        badgeContent={getCurrentBadFindingLinkCount()}
                        sx={{
                            ".MuiBadge-badge": badgeStyle,
                        }}
                    >
                        {insightThumbsDownIcon}
                    </Badge>
                ) : (
                    insightThumbsDownIcon
                )}
                
            </IconButton>
        </Tooltip>
    );

    const handleLinkedEntryDisplayFeature = () => {
        const { entry } = props;

        if (canvasState?.selectedHypothesisIDToFilter === entry?.entryID) {
            setSelectedHypothesisIDToFilter(setCanvasState, "");
        } else {
            setSelectedHypothesisIDToFilter(setCanvasState, entry?.entryID);
        }
    };

    const linkedHypothesesButtonTooltipLabel = () => {
        const { entry } = props;
        const { name: categoryName } = entry?.category;
        const { selectedHypothesisIDToFilter } = canvasState;

        if (categoryName === CUSTOMER_SEGMENTS_CATEGORY_NAME) {
            if (!!selectedHypothesisIDToFilter) {
                return "Click to show all Hypotheses";
            }

            return "Click to only show linked Hypotheses";
        }

        if (!canEditCanvas) {
            if (countOfLinkedEntries > 0) {
                return "View Linked Customer Segments";
            } else {
                return "No Linked Customer Segments";
            }
        }
        if(canvasData?.type==="DThreeCanvas"){
            return "Link Hypothesis to Customer Segments and Stakeholders";
        }

        return "Link Hypothesis to Customer Segments";
    };

    const countOfLinkedEntries = React.useMemo(() => {
        let linkedEntryIDs = props?.entry?.linkedEntryIDs || [];

        linkedEntryIDs = linkedEntryIDs?.filter((id) => id !== props?.entry?.entryID);
        linkedEntryIDs = new Set(linkedEntryIDs);

        return linkedEntryIDs.size;
    }, [props.entry.entryID, props?.entry?.linkedEntryIDs]);

    const hypothesisLinkButton = (
      <IconButton
        size="small"
        sx={{...boxStyle,}}
        disableRipple
        onClick={openLinkPopover}
      >
        {props?.entry?.category?.name === "Customer Segments" ? (
          canvasState?.selectedHypothesisIDToFilter === "" ? (
            <LinikedShowHypothesesIcon
              sx={{
                 margin: "19% 0px 0px 0px",
                color: "black",
                fill: "none",
                "&:hover": {
                  color: "#8753B8",
                },
              }}
            />
          ) : (
            <LinikedHideHypothesesIcon
              sx={{
                 margin: "12% 0px 0px 0px",
                color: "black",
                fill: "none",
                "&:hover": {
                  color: "#8753B8",
                },
              }}
            />
          )
        ) : (
            <LinkIcon
                strokeWidth="1.2"
                hovered={linkHoverd}
                onMouseEnter={() => setLinkHoverd(true)}
                onMouseLeave={() => setLinkHoverd(false)}
                sx={{
                    margin: "3% 0px 0px 0px",
                    color: "black",
                    fill:"none",
                    "&:hover": {
                        color: colors.CANVAS_LINK_PURPLE,
                    },
                }}
            />          
        )}
      </IconButton>
    );

    const HYPOTHESIS_ACTION_TOOLBAR_ITEM = (
        <Tooltip
            placement="top"
            title={linkedHypothesesButtonTooltipLabel()}
            PopperProps={{
                sx: {
                    ...tooltipCommonAttributes,
                    margin: "8px 0px !important",
                    padding: "0px !important",
                    "& .MuiTooltip-tooltip": {
                        margin: "0px !important",
                    },
                },
            }}
        >
            {countOfLinkedEntries > 0 ? (
                <Badge
                    badgeContent={countOfLinkedEntries}
                    sx={{
                        "& .MuiBadge-badge": {
                            ...badgeStyle,
                            top: "0px", 
                            right:'4px'
                          },
                    }}
                >
                    {hypothesisLinkButton}
                </Badge>
            ) : hypothesisLinkButton}
        </Tooltip>
    );

    const handleOnDeleteEntry = () => {
        handleEntryStateChange("Deleted");
    };

    const DELETE_ACTION_TOOLBAR_ITEM = (
        <Tooltip
            placement="top"
            title="Delete"
            PopperProps={{
                sx: {
                    ...tooltipCommonAttributes,
                    margin: "12px 0px !important",
                    padding: "0px !important",
                    "& .MuiTooltip-tooltip": {
                        margin: "0px !important",
                    },
                },
            }}
        >
            <IconButton
                disableRipple
                 sx={{ ...boxStyle,width:"28px",margin:"0px"}}
                onClick={handleOnDeleteEntry}
            >
                <DeleteOutlinedIcon sx={{
              color: "black",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            </IconButton>
        </Tooltip>
    );

    return (
        <span style={actionToolbarStyle}>
            <Snackbar
                open={localError !== null}
                autoHideDuration={6000}
                onClose={() => {
                    setLocalError(null);
                }}
                message={localError}
            />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={0}
                sx={{ height: "100%",padding: "3px 0 0 0"}}
            >
                {COMMENT_ACTION_TOOLBAR_ITEM}
                {INSIGHT_ACTION_TOOLBAR_ITEM}
                {HYPOTHESIS_ACTION_TOOLBAR_ITEM}
                {canEditCanvas && VALIDATE_ACTION_TOOLBAR_ITEM}
                {canEditCanvas && INVALIDATE_ACTION_TOOLBAR_ITEM}
                {canEditCanvas && DELETE_ACTION_TOOLBAR_ITEM}
            </Stack>
        </span>
    );
}